import React from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  ReferenceField,
  FunctionField,
  TopToolbar,
  EditButton,
  DeleteButton,
  ListButton,
  useRecordContext,
  Labeled,
} from 'react-admin';
import {
  Card,
  Typography,
  Box,
  Chip,
  Grid,
  Divider,
  CardContent,
  Stack,
  Paper,
} from '@mui/material';
import {
  Input as InputIcon,
  TableChart as TableIcon,
  LocationOn as ZoneIcon,
  Factory as PlantIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  List as ListIcon,
} from '@mui/icons-material';

const ShowTitle = () => {
  const record = useRecordContext();
  return record
    ? `Input: ${record.variableName || record.name}`
    : 'Input';
};

const InputShowActions = () => (
  <TopToolbar>
    <EditButton icon={<EditIcon />} />
    <DeleteButton icon={<DeleteIcon />} />
    <ListButton icon={<ListIcon />} />
  </TopToolbar>
);

const InputsShow = () => (
  <Show title={<ShowTitle />} actions={<InputShowActions />}>
    <Box sx={{ padding: 4 }}>
      {/* Header Section */}
      <Paper
        elevation={1}
        sx={{
          p: 3,
          mb: 3,
          bgcolor: 'background.paper',
          borderRadius: 1,
          borderLeft: 4,
          borderLeftColor: 'primary.main',
        }}
      >
        <Box display='flex' alignItems='center'>
          <InputIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant='h5' component='h1' fontWeight='600'>
              Detalles del Input
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Información completa del input SCADA
            </Typography>
          </Box>
        </Box>
      </Paper>

      <SimpleShowLayout>
        <Grid container spacing={3}>
          {/* Información Básica */}
          <Grid item xs={12} lg={6}>
            <Card
              elevation={1}
              sx={{
                borderRadius: 1,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <InputIcon
                    sx={{ mr: 1.5, color: 'text.secondary', fontSize: 20 }}
                  />
                  <Typography variant='h6' fontWeight='500'>
                    Información Básica
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2}>
                  <Labeled label='ID del Input'>
                    <TextField
                      source='id'
                      sx={{
                        '& .MuiTypography-root': {
                          fontWeight: 500,
                          color: 'text.primary',
                        },
                      }}
                    />
                  </Labeled>

                  <Labeled label='Nombre del Input'>
                    <TextField
                      source='variableName'
                      sx={{
                        '& .MuiTypography-root': {
                          fontWeight: 600,
                          color: 'text.primary',
                        },
                      }}
                    />
                  </Labeled>

                  <Labeled label='Tipo de Variable'>
                    <TextField
                      source='varType'
                      sx={{
                        '& .MuiTypography-root': {
                          fontWeight: 400,
                          color: 'text.primary',
                        },
                      }}
                    />
                  </Labeled>

                  <Labeled label='Descripción'>
                    <TextField
                      source='description'
                      sx={{
                        '& .MuiTypography-root': {
                          color: 'text.secondary',
                        },
                      }}
                    />
                  </Labeled>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Relaciones Jerárquicas */}
          <Grid item xs={12} lg={6}>
            <Card
              elevation={1}
              sx={{
                borderRadius: 1,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TableIcon
                    sx={{ mr: 1.5, color: 'text.secondary', fontSize: 20 }}
                  />
                  <Typography variant='h6' fontWeight='500'>
                    Relaciones Jerárquicas
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2}>
                  <Labeled label='Tabla'>
                    <ReferenceField
                      source='tableId'
                      reference='tables'
                      link='show'
                    >
                      <FunctionField
                        render={(record: any) => {
                          if (!record)
                            return (
                              <Chip
                                label='Sin tabla'
                                variant='outlined'
                                size='small'
                              />
                            );
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <TableIcon
                                sx={{
                                  mr: 1,
                                  fontSize: 16,
                                  color: 'text.secondary',
                                }}
                              />
                              <Chip
                                label={record.tableName || 'Tabla sin nombre'}
                                variant='outlined'
                                size='small'
                              />
                            </Box>
                          );
                        }}
                      />
                    </ReferenceField>
                  </Labeled>

                  <Labeled label='Zona'>
                    <ReferenceField source='tableId' reference='tables'>
                      <ReferenceField
                        source='zoneId'
                        reference='zones'
                        link='show'
                      >
                        <FunctionField
                          render={(record: any) => {
                            if (!record)
                              return (
                                <Chip
                                  label='Sin zona'
                                  variant='outlined'
                                  size='small'
                                />
                              );
                            return (
                              <Box
                                sx={{ display: 'flex', alignItems: 'center' }}
                              >
                                <ZoneIcon
                                  sx={{
                                    mr: 1,
                                    fontSize: 16,
                                    color: 'text.secondary',
                                  }}
                                />
                                <Chip
                                  label={record.zoneName || 'Zona sin nombre'}
                                  variant='outlined'
                                  size='small'
                                />
                              </Box>
                            );
                          }}
                        />
                      </ReferenceField>
                    </ReferenceField>
                  </Labeled>

                  <Labeled label='Planta'>
                    <ReferenceField source='tableId' reference='tables'>
                      <ReferenceField source='zoneId' reference='zones'>
                        <ReferenceField
                          source='plantId'
                          reference='plants'
                          link='show'
                        >
                          <FunctionField
                            render={(record: any) => {
                              if (!record)
                                return (
                                  <Chip
                                    label='Sin planta'
                                    variant='outlined'
                                    size='small'
                                  />
                                );
                              return (
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center' }}
                                >
                                  <PlantIcon
                                    sx={{
                                      mr: 1,
                                      fontSize: 16,
                                      color: 'text.secondary',
                                    }}
                                  />
                                  <Chip
                                    label={
                                      record.plantName || 'Planta sin nombre'
                                    }
                                    variant='outlined'
                                    size='small'
                                  />
                                </Box>
                              );
                            }}
                          />
                        </ReferenceField>
                      </ReferenceField>
                    </ReferenceField>
                  </Labeled>
                </Stack>

                {/* Jerarquía Visual */}
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ mb: 1, display: 'block', fontWeight: 500 }}
                  >
                    Jerarquía:
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <PlantIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                    <Typography variant='caption'>Planta</Typography>
                    <Typography
                      variant='caption'
                      sx={{ mx: 0.5, color: 'text.disabled' }}
                    >
                      →
                    </Typography>
                    <ZoneIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                    <Typography variant='caption'>Zona</Typography>
                    <Typography
                      variant='caption'
                      sx={{ mx: 0.5, color: 'text.disabled' }}
                    >
                      →
                    </Typography>
                    <TableIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                    <Typography variant='caption'>Tabla</Typography>
                    <Typography
                      variant='caption'
                      sx={{ mx: 0.5, color: 'text.disabled' }}
                    >
                      →
                    </Typography>
                    <InputIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                    <Typography variant='caption' fontWeight='500'>
                      Input
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </SimpleShowLayout>
    </Box>
  </Show>
);

export default InputsShow;